import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ServerCrash, UserRoundCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { useModalProvider } from '@/common/hooks/useModalProvider'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

async function getReactivateUser(userId: string) {
  await api.backend.get(`/user/reactivate/${userId}`)
}

export function useReactivateUser() {
  const { t } = useTranslation('registers')
  const { handleModal, toggleLoading } = useModalProvider()

  const { mutateAsync } = useMutation<unknown, AxiosError<ApiError>, string>({
    mutationKey: ['/user/reactivate/id'],
    mutationFn: getReactivateUser,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: UserRoundCheck,
          title: t('users.reactivate_user.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/user/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('users.reactivate_user.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('users.reactivate_user.toast_error.description'),
        },
      })
    },
  })

  const handleReactivateUser = (userId: string) => {
    handleModal({
      type: 'warning',
      title: t('users.reactivate_user.modal.title'),
      description: t('users.reactivate_user.modal.description'),
      confirmText: t('users.reactivate_user.modal.button_confirm'),
      onConfirm: async () => {
        toggleLoading()
        await mutateAsync(userId).finally(() => {
          toggleLoading()
        })
      },
    })
  }

  return {
    handleReactivateUser,
  }
}
